import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert, Badge, Button, Card, HelpTip, Input, PageHeader } from '@/components/ui';
import { api } from '@/lib/api';
import { FreioOmie } from './FreioOmie';

import { formatDataHoraSegundos } from '@/lib/format-date';
/**
 * /admin/integracoes — visão central das integrações externas (B2.1 #7).
 *
 * Antes: o grupo "Integrações" do menu admin caía direto no dashboard de
 * Indicadores Financeiros, porque o primeiro item do grupo era
 * `/admin/financeiro/dashboard` (o GroupTab navega pro primeiro item). Esta
 * tela passa a ser o destino do grupo: um overview de cada integração (Omie,
 * Google) com status, último sync e "testar conexão".
 *
 * Regra de segurança: NUNCA expõe credencial em claro. As credenciais vivem no
 * `.env` da VPS — aqui mostramos apenas se estão PRESENTES (empresa aparece em
 * `empresasComCredenciais`) e se a conexão responde ("testar conexão").
 *
 * Não duplica as telas detalhadas: linka pra elas (Sync Omie, Audit, Produtos).
 */

interface OmieSyncStatus {
  writeMode: 'disabled' | 'dry_run' | 'live';
  diasReadOnly: number;
  peloMenos1SyncCadaTipo: boolean;
  empresasComCredenciais: string[];
  ultimosSync: Array<{
    empresa_id: string;
    tipo: string;
    ultimo_iniciado: string;
    ultimo_finalizado: string;
  }>;
}

interface CredsTestResult {
  empresaId: string;
  ok: boolean;
  status: string;
  message: string;
}

// FASE-C: o modo de escrita deixou de aparecer como "disabled/dry_run/live"
// (nome interno) e virou uma frase que o admin entende.
const WRITE_MODE: Record<
  OmieSyncStatus['writeMode'],
  { label: string; variant: 'neutral' | 'warning' | 'danger' }
> = {
  disabled: { label: 'Envio desligado', variant: 'neutral' },
  dry_run: { label: 'Envio em simulação', variant: 'warning' },
  live: { label: 'Envio ligado', variant: 'danger' },
};

const AJUDA_ENVIO =
  'Diz se o IGM pode gravar dados no Omie. Desligado: o IGM só lê. ' +
  'Em simulação: o envio é registrado mas nada chega no Omie. ' +
  'Ligado: o dado é gravado no Omie de verdade.';

function formatIso(iso: string | undefined | null): string {
  if (!iso) return 'nunca';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'nunca';
  return formatDataHoraSegundos(d);
}

export function PaginaOmie() {
  // ─── Omie ──────────────────────────────────────────────────────────────
  const [omie, setOmie] = useState<OmieSyncStatus | null>(null);
  const [omieErr, setOmieErr] = useState<string | null>(null);
  const [omieLoading, setOmieLoading] = useState(true);
  const [testando, setTestando] = useState<string | null>(null);
  const [credsResult, setCredsResult] = useState<Record<string, CredsTestResult>>({});

  function loadOmie() {
    setOmieErr(null);
    setOmieLoading(true);
    api
      .get<OmieSyncStatus>('/api/v1/admin/omie/sync/status')
      .then(setOmie)
      .catch((e: Error) => setOmieErr(e.message))
      .finally(() => setOmieLoading(false));
  }
  useEffect(loadOmie, []);

  async function testarOmie(empresaId: string) {
    setTestando(empresaId);
    try {
      const r = await api.post<CredsTestResult>('/api/v1/admin/omie/test-credentials', {
        empresaId,
      });
      setCredsResult((prev) => ({ ...prev, [empresaId]: r }));
    } catch (e) {
      setCredsResult((prev) => ({
        ...prev,
        [empresaId]: { empresaId, ok: false, status: 'erro', message: (e as Error).message },
      }));
    } finally {
      setTestando(null);
    }
  }

  const ultimoSyncGeral = (omie?.ultimosSync ?? [])
    .map((u) => u.ultimo_finalizado)
    .filter((v): v is string => Boolean(v))
    .sort()
    .pop();

  // ── Cadência do pull (17/08 — configurável sem deploy, mig 474) ─────────
  const [cadencia, setCadencia] = useState<number | null>(null);
  const [cadenciaDraft, setCadenciaDraft] = useState('');
  const [cadenciaBusy, setCadenciaBusy] = useState(false);
  const [cadenciaMsg, setCadenciaMsg] = useState<string | null>(null);
  useEffect(() => {
    api
      .get<{ intervaloMinutos: number }>('/api/v1/admin/omie/sync-cadencia')
      .then((r) => {
        setCadencia(r.intervaloMinutos);
        setCadenciaDraft(String(r.intervaloMinutos));
      })
      .catch(() => {});
  }, []);
  // ── Frente 6: os horários da conferência completa ──────────────────
  const [janelas, setJanelas] = useState<{ horas: number[]; minHoras: number } | null>(null);
  const [janelasDraft, setJanelasDraft] = useState('');
  const [minHorasDraft, setMinHorasDraft] = useState('');
  const [janelasBusy, setJanelasBusy] = useState(false);
  const [janelasMsg, setJanelasMsg] = useState<string | null>(null);
  useEffect(() => {
    api
      .get<{ horas: number[]; minHoras: number }>('/api/v1/admin/omie/varredura-completa')
      .then((r) => {
        setJanelas(r);
        setJanelasDraft(r.horas.join(', '));
        setMinHorasDraft(String(r.minHoras));
      })
      .catch(() => {});
  }, []);

  async function salvarJanelas() {
    // ⚠️ O formulário não é surdo: cada recusa diz o que está errado, e diz
    // no texto que a pessoa acabou de digitar.
    const horas = janelasDraft
      .split(',')
      .map((x) => x.trim())
      .filter(Boolean)
      .map(Number);
    if (!horas.length || horas.some((h) => !Number.isInteger(h) || h < 0 || h > 23)) {
      setJanelasMsg('Escreva as horas separadas por vírgula, cada uma de 0 a 23. Exemplo: 2, 10, 15');
      return;
    }
    const minHoras = Number(minHorasDraft);
    if (!Number.isInteger(minHoras) || minHoras < 1 || minHoras > 24) {
      setJanelasMsg('A distância mínima entre conferências vai de 1 a 24 horas.');
      return;
    }
    setJanelasBusy(true);
    setJanelasMsg(null);
    try {
      const r = await api.put<{ horas: number[]; minHoras: number }>(
        '/api/v1/admin/omie/varredura-completa',
        { horas, minHoras },
      );
      setJanelas(r);
      setJanelasDraft(r.horas.join(', '));
      setMinHorasDraft(String(r.minHoras));
      setJanelasMsg('Salvo — vale a partir do próximo ciclo, sem reiniciar nada.');
    } catch (e) {
      setJanelasMsg(`Não foi possível salvar: ${(e as Error).message}`);
    } finally {
      setJanelasBusy(false);
    }
  }

  async function salvarCadencia() {
    const v = Number(cadenciaDraft);
    if (!Number.isInteger(v) || v < 5 || v > 120) {
      setCadenciaMsg('A frequência precisa ser um número entre 5 e 120 minutos.');
      return;
    }
    setCadenciaBusy(true);
    setCadenciaMsg(null);
    try {
      const r = await api.put<{ intervaloMinutos: number }>('/api/v1/admin/omie/sync-cadencia', {
        intervaloMinutos: v,
      });
      setCadencia(r.intervaloMinutos);
      setCadenciaMsg('Salvo — vale a partir do próximo ciclo, sem reiniciar nada.');
    } catch (e) {
      setCadenciaMsg(`Não foi possível salvar: ${(e as Error).message}`);
    } finally {
      setCadenciaBusy(false);
    }
  }

  return (
    <section className="min-w-0 space-y-6" data-testid="integracao-omie-page">
      <PageHeader
        className="mb-0"
        crumb="Configurações · Integrações"
        title="Omie"
        subtitle="Sistema financeiro — clientes, produtos, pedidos e contas. As senhas ficam guardadas no servidor; aqui você vê se elas existem e se a conexão responde."
      />

      {/* ─── Omie ─── */}
      <Card className="p-4 sm:p-5 space-y-4" data-testid="integracao-omie">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="font-display text-xl text-ink-1 dark:text-ink-dark-1">Omie</h2>
            <p className="text-xs text-ink-3 dark:text-ink-dark-3">
              Sistema financeiro — clientes, produtos, pedidos e contas.
            </p>
          </div>
          {omie && (
            <span className="inline-flex items-center gap-1.5">
              <Badge variant={WRITE_MODE[omie.writeMode].variant} size="sm">
                {WRITE_MODE[omie.writeMode].label}
              </Badge>
              <HelpTip texto={AJUDA_ENVIO} />
            </span>
          )}
        </div>

        {omieErr && (
          <Alert variant="danger" rail title="Não foi possível carregar a situação do Omie">
            Tente recarregar a página. Se continuar, avise quem cuida do servidor.
            <span className="mt-1 block break-words text-xs opacity-80">Detalhe: {omieErr}</span>
          </Alert>
        )}

        {omieLoading ? (
          <div className="text-xs text-ink-3 dark:text-ink-dark-3 italic py-2">
            Carregando a situação do Omie…
          </div>
        ) : omie ? (
          <>
            <dl className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
              <div className="min-w-0">
                <dt className="text-xs text-ink-3 dark:text-ink-dark-3">Empresas com acesso</dt>
                <dd className="text-ink-1 dark:text-ink-dark-1 font-medium">
                  {omie.empresasComCredenciais.length}
                </dd>
              </div>
              <div className="min-w-0">
                <dt className="text-xs text-ink-3 dark:text-ink-dark-3">Última sincronização</dt>
                <dd className="text-ink-1 dark:text-ink-dark-1 font-medium break-words">
                  {formatIso(ultimoSyncGeral)}
                </dd>
              </div>
              <div className="min-w-0">
                <dt className="text-xs text-ink-3 dark:text-ink-dark-3">
                  Todos os tipos já sincronizaram
                </dt>
                <dd className="text-ink-1 dark:text-ink-dark-1 font-medium">
                  {omie.peloMenos1SyncCadaTipo ? 'Sim' : 'Ainda não'}
                </dd>
              </div>
            </dl>

            {/* 🔴 O freio virou componente porque aparece TAMBÉM na Visão geral —
                é o botão que se procura com pressa, e não pode ganhar um clique.
                Mesmo componente, mesmo endpoint: não são dois controles. */}
            <FreioOmie />

            {/* Cadência do pull (17/08): o número pode mudar sem deploy. */}
            <div
              className="rounded-xl border border-line dark:border-line-dark p-3 space-y-2"
              data-testid="omie-cadencia"
            >
              <span className="inline-flex items-center gap-1.5">
                <span className="text-sm font-medium text-ink-1 dark:text-ink-dark-1">
                  Frequência da sincronização (minutos)
                </span>
                <HelpTip texto="De quanto em quanto tempo o sistema busca os dados no Omie (contas, extrato, vendas — as duas empresas). O ciclo completo leva de 5 a 6 minutos em condições normais; frequência MENOR que isso faz ciclos serem pulados pela trava de sobreposição. A recomendação medida é 10 minutos ou mais. Mudar aqui vale a partir do próximo ciclo, sem reiniciar nada." />
              </span>
              <div className="flex items-center gap-2">
                <Input
                  size="sm"
                  type="number"
                  min={5}
                  max={120}
                  value={cadenciaDraft}
                  onChange={(e) => setCadenciaDraft(e.target.value)}
                  aria-label="Frequência da sincronização em minutos"
                  data-testid="omie-cadencia-input"
                  className="max-w-[110px]"
                />
                <Button
                  size="sm"
                  variant="secondary"
                  loading={cadenciaBusy}
                  disabled={cadencia !== null && Number(cadenciaDraft) === cadencia}
                  onClick={() => void salvarCadencia()}
                  data-testid="omie-cadencia-salvar"
                >
                  Salvar
                </Button>
                {cadencia !== null && (
                  <span className="text-xs text-ink-3 dark:text-ink-dark-3">atual: {cadencia} min</span>
                )}
              </div>
              {cadenciaMsg && (
                <p className="text-xs text-ink-2 dark:text-ink-dark-2">{cadenciaMsg}</p>
              )}
            </div>

            {/* Frente 6 (01/09): quando a varredura COMPLETA roda. */}
            <div className="flex flex-col gap-2 rounded-xl border border-line dark:border-line-dark p-4">
              <span className="inline-flex items-center gap-1.5">
                <span className="text-sm font-medium text-ink-1 dark:text-ink-dark-1">
                  Conferência completa com o Omie (horários)
                </span>
                <HelpTip texto="A sincronização normal só traz o que MUDOU no Omie — e registro apagado lá não volta como alteração, então o painel vai acumulando títulos que já não existem. A conferência completa relê tudo e limpa esses restos. Ela custa cerca de 3 minutos e 21 chamadas ao Omie por rodada. Escreva os horários separados por vírgula, em hora de Brasília." />
              </span>
              <div className="flex flex-wrap items-center gap-2">
                <Input
                  size="sm"
                  value={janelasDraft}
                  onChange={(e) => setJanelasDraft(e.target.value)}
                  aria-label="Horários da conferência completa"
                  placeholder="2, 10, 15"
                  data-testid="omie-janelas-input"
                  className="max-w-[160px]"
                />
                <span className="text-xs text-ink-3 dark:text-ink-dark-3">distância mínima</span>
                <Input
                  size="sm"
                  type="number"
                  min={1}
                  max={24}
                  value={minHorasDraft}
                  onChange={(e) => setMinHorasDraft(e.target.value)}
                  aria-label="Distância mínima entre conferências, em horas"
                  data-testid="omie-janelas-min"
                  className="max-w-[80px]"
                />
                <span className="text-xs text-ink-3 dark:text-ink-dark-3">h</span>
                <Button
                  size="sm"
                  variant="secondary"
                  loading={janelasBusy}
                  onClick={() => void salvarJanelas()}
                  data-testid="omie-janelas-salvar"
                >
                  Salvar
                </Button>
                {janelas && (
                  <span className="text-xs text-ink-3 dark:text-ink-dark-3">
                    atual: {janelas.horas.map((h) => `${String(h).padStart(2, '0')}h`).join(' · ')}
                  </span>
                )}
              </div>
              {/* 🔴 O comportamento que o Matheus pediu para ficar explícito: a
                  janela perdida PULA, não empilha. Sem isto dito na tela, um
                  servidor que ficou fora pareceria "dever" conferências. */}
              <p className="text-xs text-ink-2 dark:text-ink-dark-2">
                Se o servidor estiver fora no horário marcado, aquela conferência é{' '}
                <strong>pulada</strong> — ela não fica acumulada para rodar depois, e o registro
                fica no histórico de sincronizações. Se passar de 30 horas sem nenhuma conferência
                completa, o sistema faz uma na hora, fora dos horários.
              </p>
              {janelasMsg && <p className="text-xs text-ink-2 dark:text-ink-dark-2">{janelasMsg}</p>}
            </div>

            {omie.empresasComCredenciais.length === 0 ? (
              <Alert variant="info" rail title="Nenhuma empresa liberada pra falar com o Omie">
                Peça a quem cuida do servidor pra preencher <code>OMIE_APP_KEY_*</code> e{' '}
                <code>OMIE_APP_SECRET_*</code> no arquivo <code>.env</code>. Enquanto isso não
                acontecer, nenhuma sincronização roda.
              </Alert>
            ) : (
              <ul className="space-y-2" data-testid="omie-empresas">
                {omie.empresasComCredenciais.map((empresaId) => {
                  const r = credsResult[empresaId];
                  return (
                    <li
                      key={empresaId}
                      className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border border-line dark:border-line-dark px-3 py-2"
                      data-testid={`omie-empresa-${empresaId}`}
                    >
                      <span className="min-w-0 break-words font-mono text-xs text-ink-1 dark:text-ink-dark-1">
                        {empresaId}
                      </span>
                      <Badge variant="success" size="sm">
                        Acesso configurado
                      </Badge>
                      <div className="ml-auto flex flex-wrap items-center gap-2">
                        {r && (
                          <Badge
                            variant={r.ok ? 'success' : 'danger'}
                            size="sm"
                            data-testid={`omie-creds-result-${empresaId}`}
                          >
                            {r.ok ? 'Conexão respondeu' : 'Conexão falhou'}
                          </Badge>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          loading={testando === empresaId}
                          onClick={() => testarOmie(empresaId)}
                          data-testid={`omie-testar-${empresaId}`}
                        >
                          Testar conexão
                        </Button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}

            <div className="flex flex-wrap gap-x-4 gap-y-2 text-sm pt-1">
              <Link to="/admin/omie/sync-status" className="text-red hover:underline">
                Ver a sincronização em detalhe
              </Link>
              <Link to="/admin/omie/audit-log" className="text-red hover:underline">
                Registro de envios ao Omie
              </Link>
              <Link to="/admin/produtos/conciliar" className="text-red hover:underline">
                Conciliação de produtos
              </Link>
            </div>
          </>
        ) : null}
      </Card>
    </section>
  );
}
